import { QueryClient, type QueryKey } from '@tanstack/react-query';

import { ApiError } from '../api/client';

const DEFAULT_STALE_TIME_MS = 30_000;
const DEFAULT_GC_TIME_MS = 5 * 60_000;
const MAX_QUERY_RETRIES = 2;

function shouldRetryQuery(failureCount: number, error: Error) {
  if (failureCount >= MAX_QUERY_RETRIES) {
    return false;
  }

  if (!(error instanceof ApiError)) {
    return failureCount < 1;
  }

  return (
    error.status === 0 ||
    error.status === 408 ||
    error.status === 429 ||
    error.status >= 500
  );
}

export function createAppQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        retry: shouldRetryQuery,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryKeys = {
  all: ['flow-wizard'] as const,
  system: {
    all: ['flow-wizard', 'system'] as const,
    health: ['flow-wizard', 'system', 'health'] as const,
    readiness: ['flow-wizard', 'system', 'readiness'] as const,
    version: ['flow-wizard', 'system', 'version'] as const,
  },
  resource: (resourceName: string) => {
    const root = ['flow-wizard', resourceName] as const;
    return {
      all: root,
      lists: [...root, 'list'] as const,
      list: (filters: Readonly<Record<string, unknown>>) =>
        [...root, 'list', filters] as const,
      detail: (id: string) => [...root, 'detail', id] as const,
    };
  },
} satisfies Record<string, unknown>;

export function invalidateResource(
  queryClient: QueryClient,
  resourceKey: QueryKey,
) {
  return queryClient.invalidateQueries({ queryKey: resourceKey });
}
